import { useEffect, useState } from "react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { toast } from "sonner";
import api, { errText } from "@/lib/api";
import CrmShell from "@/components/crm/CrmShell";
import { Panel, money } from "@/components/crm/ui";

export default function Attribution() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api
      .get("/dashboard/attribution")
      .then(({ data: d }) => setData(d))
      .catch((e) => toast.error(errText(e)));
  }, []);

  return (
    <CrmShell
      title="Google Ads attribution"
      subtitle="Which campaigns and keywords actually produce bookings"
    >
      {!data ? (
        <p className="text-sm text-body">Loading…</p>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Panel data-testid="attr-kpi-gclid">
              <div className="text-xs font-bold uppercase tracking-wide text-muted2">Paid-click leads</div>
              <div className="mt-3 font-display text-2xl font-semibold">{data.gclid_leads}</div>
              <div className="mt-1 text-[11px] text-muted2">with GCLID captured</div>
            </Panel>
            <Panel>
              <div className="text-xs font-bold uppercase tracking-wide text-muted2">Campaigns</div>
              <div className="mt-3 font-display text-2xl font-semibold">{data.campaigns.length}</div>
            </Panel>
            <Panel>
              <div className="text-xs font-bold uppercase tracking-wide text-muted2">Mobile leads</div>
              <div className="mt-3 font-display text-2xl font-semibold">{data.devices.mobile || 0}</div>
              <div className="mt-1 text-[11px] text-muted2">{data.devices.desktop || 0} on desktop</div>
            </Panel>
            <Panel>
              <div className="text-xs font-bold uppercase tracking-wide text-muted2">Attributed value</div>
              <div className="mt-3 font-display text-2xl font-semibold">
                {money(data.campaigns.reduce((s, c) => s + (c.value || 0), 0))}
              </div>
            </Panel>
          </div>

          <Panel data-testid="attr-campaign-table">
            <div className="text-xs font-bold uppercase tracking-wide text-muted2">
              Campaign performance
            </div>
            <div className="mt-4 overflow-x-auto">
              <table className="w-full min-w-[620px] text-sm">
                <thead>
                  <tr className="text-left text-xs font-semibold uppercase tracking-wide text-muted2">
                    <th className="pb-3">Campaign</th>
                    <th className="pb-3">Source / Medium</th>
                    <th className="pb-3">Leads</th>
                    <th className="pb-3">Closed</th>
                    <th className="pb-3">Conv.</th>
                    <th className="pb-3">Value</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {data.campaigns.map((c) => (
                    <tr key={`${c.campaign}-${c.source}`}>
                      <td className="py-3 pr-4 text-ink2">{c.campaign}</td>
                      <td className="py-3 pr-4 text-[11px] text-body">
                        {c.source} / {c.medium}
                      </td>
                      <td className="py-3 text-body">{c.leads}</td>
                      <td className="py-3 text-body">{c.closed}</td>
                      <td className="py-3 text-brand">{c.conversion}%</td>
                      <td className="py-3 text-body">{c.value ? money(c.value) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>

          <Panel data-testid="attr-keyword-chart">
            <div className="text-xs font-bold uppercase tracking-wide text-muted2">
              Top converting keywords
            </div>
            <div className="mt-5 h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.keywords} layout="vertical" margin={{ left: 12 }}>
                  <CartesianGrid stroke="#EFF0F1" horizontal={false} />
                  <XAxis type="number" tick={{ fill: "#6b7280", fontSize: 10 }} axisLine={false} tickLine={false} />
                  <YAxis
                    type="category"
                    dataKey="keyword"
                    tick={{ fill: "#62646A", fontSize: 10 }}
                    width={190}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    contentStyle={{ background: "#FFFFFF", border: "1px solid #E4E5E7", borderRadius: 10, fontSize: 12 }}
                  />
                  <Bar dataKey="leads" fill="#1DBF73" radius={[0, 4, 4, 0]} barSize={14} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Panel>

          <p className="text-[11px] text-muted2">
            Conversion tracking hook is wired into the landing page form and activates as soon as a
            Google Ads Conversion ID and Label are added in <span className="font-mono">src/config/brand.js</span>.
          </p>
        </div>
      )}
    </CrmShell>
  );
}
